/** @jsxImportSource @emotion/react */
import { useState, useRef } from "react";
import { Card } from "primereact/card";
import { Column } from "primereact/column";
import { Menu } from "primereact/menu";
import { Sidebar } from "../../../shared";
import { Toast } from "primereact/toast";
import { confirmDialog } from "primereact/confirmdialog";
import {
  Boton,
  Titulo,
  CampoInformacion,
  Formulario,
  formatFecha,
  accessAPI,
  useIsMobile,
  TablaFiltrable
} from "../../../shared";

export default function TarjetaReportesEnsayo({
  proceso,
  testReports,
  onReporteAgregado,
  onReporteEliminado
}) {
  const [sidebarAgregar, setSidebarAgregar] = useState(false);
  const [sidebarDetalle, setSidebarDetalle] = useState(false);
  const [sidebarEditar, setSidebarEditar] = useState(false);
  const [reporteSeleccionado, setReporteSeleccionado] = useState(null);
  const [loaderAgregar, setLoaderAgregar] = useState(false);
  const [loaderEditar, setLoaderEditar] = useState(false);

  const toast = useRef(null);
  const menuRef = useRef(null);
  const isMobile = useIsMobile();

  function handleEliminarReporte(reporte) {
    accessAPI(
      "DELETE",
      `admin/proceso/reporteensayo/${reporte.id}`,
      null,
      () => {
        toast.current?.show({
          severity: "success",
          summary: "Éxito",
          detail: "Reporte de ensayo eliminado"
        });
        if (onReporteEliminado) {
          onReporteEliminado(reporte.id);
        }
      },
      (error) => {
        toast.current?.show({
          severity: "error",
          summary: "Error",
          detail: error[0]?.msg || "Error al eliminar reporte",
          sticky: true
        });
      }
    );
  }

  const getMenuItems = () => {
    if (!reporteSeleccionado) return [];
    return [
      {
        label: "Ver detalles",
        icon: "pi pi-eye",
        command: () => setSidebarDetalle(true)
      },
      {
        label: "Editar",
        icon: "pi pi-pencil",
        command: () => setSidebarEditar(true)
      },
      {
        label: "Eliminar",
        icon: "pi pi-trash",
        command: () => {
          confirmDialog({
            message:
              "¿Está seguro que desea eliminar este reporte de ensayo?",
            header: "Confirmar eliminación",
            icon: "pi pi-exclamation-triangle",
            acceptLabel: "Sí",
            rejectLabel: "No",
            accept: () => handleEliminarReporte(reporteSeleccionado)
          });
        }
      }
    ];
  };

  function handleAgregarReporte(camposFormulario) {
    setLoaderAgregar(true);
    const payload = {
      ...camposFormulario,
      procesoid: proceso.id
    };
    accessAPI(
      "POST",
      "admin/proceso/reporteensayo",
      payload,
      (respuesta) => {
        setLoaderAgregar(false);
        setSidebarAgregar(false);
        if (onReporteAgregado) {
          onReporteAgregado(respuesta);
        }
      },
      (error) => {
        setLoaderAgregar(false);
        toast.current?.show({
          severity: "error",
          summary: "Error",
          detail: error[0]?.msg || "Error al agregar reporte",
          sticky: true
        });
      }
    );
  }

  function handleEditarReporte(camposFormulario) {
    setLoaderEditar(true);
    accessAPI(
      "PUT",
      `admin/proceso/reporteensayo/${reporteSeleccionado.id}`,
      camposFormulario,
      (respuesta) => {
        setLoaderEditar(false);
        setSidebarEditar(false);
        toast.current?.show({
          severity: "success",
          summary: "Éxito",
          detail: "Reporte de ensayo actualizado correctamente"
        });
        // Actualizar el reporte en la lista local
        // La API devuelve [{ msg, reporteensayo }]
        const reporteActualizado = respuesta[0]?.reporteensayo;
        if (onReporteAgregado && reporteActualizado) {
          onReporteAgregado(reporteActualizado, true);
        }
      },
      (error) => {
        setLoaderEditar(false);
        toast.current?.show({
          severity: "error",
          summary: "Error",
          detail: error[0]?.msg || "Error al actualizar reporte",
          sticky: true
        });
      }
    );
  }

  const agregarFormConfig = {
    secciones: [
      {
        campos: [
          {
            nombre: "codigo",
            tipoitemformularioid: 1,
            etiqueta: "Código del reporte",
            requerido: true
          },
          {
            nombre: "fechaemision",
            tipoitemformularioid: 7,
            etiqueta: "Fecha de emisión",
            requerido: true
          },
          {
            nombre: "laboratorioid",
            tipoitemformularioid: 5,
            etiqueta: "Laboratorio",
            requerido: true,
            opciones: `admin/laboratorio?norma=${proceso?.norma?.id}`,
            sinOpcionInicial: true
          },
          {
            nombre: "actividadid",
            tipoitemformularioid: 5,
            etiqueta: "Actividad relacionada",
            requerido: true,
            opciones: `admin/actividad/proceso/${proceso.id}?dropdown=true`,
            sinOpcionInicial: true
          }
        ]
      }
    ],
    botones: [
      {
        etiqueta: "Agregar reporte",
        verde: true,
        callback: handleAgregarReporte,
        validar: true
      }
    ]
  };

  const editarFormConfig = reporteSeleccionado
    ? {
        secciones: [
          {
            campos: [
              {
                nombre: "codigo",
                tipoitemformularioid: 1,
                etiqueta: "Código del reporte",
                requerido: true,
                respuesta: reporteSeleccionado.codigo
              },
              {
                nombre: "fechaemision",
                tipoitemformularioid: 7,
                etiqueta: "Fecha de emisión",
                requerido: true,
                respuesta: reporteSeleccionado.fechaemision
                  ? new Date(reporteSeleccionado.fechaemision)
                  : null
              },
              {
                nombre: "laboratorioid",
                tipoitemformularioid: 5,
                etiqueta: "Laboratorio",
                requerido: true,
                opciones: `admin/laboratorio?norma=${proceso?.norma?.id}`,
                sinOpcionInicial: true,
                respuesta:
                  reporteSeleccionado.laboratorioid ||
                  reporteSeleccionado.laboratorio?.id
              },
              {
                nombre: "actividadid",
                tipoitemformularioid: 5,
                etiqueta: "Actividad relacionada",
                requerido: true,
                opciones: `admin/actividad/proceso/${proceso.id}?dropdown=true`,
                sinOpcionInicial: true,
                respuesta: reporteSeleccionado.actividad?.id
              }
            ]
          }
        ],
        botones: [
          {
            etiqueta: "Guardar cambios",
            callback: handleEditarReporte,
            validar: true
          }
        ]
      }
    : null;

  const accionesTemplate = (rowData) => (
    <Boton
      icono
      negro
      callback={(e) => {
        setReporteSeleccionado(rowData);
        menuRef.current.toggle(e);
      }}
    >
      <i className="pi pi-ellipsis-v" />
    </Boton>
  );

  return (
    <>
      <Toast ref={toast} position="top-center" />
      <Menu model={getMenuItems()} popup ref={menuRef} />

      <Card>
        <Titulo
          botones={
            <Boton texto negro callback={() => setSidebarAgregar(true)}>
              <i className="pi pi-plus"></i>
            </Boton>
          }
        >
          Reportes de ensayo
        </Titulo>

        {testReports && testReports.length > 0 ? (
          <TablaFiltrable conFiltros={false} value={testReports} size="small" nombreArchivo="reportes_ensayo">
            <Column field="codigo" header="Código" />
            {!isMobile && (
              <Column
                header="Fecha de emisión"
                body={(rowData) => formatFecha(rowData.fechaemision)}
              />
            )}
            <Column
              body={accionesTemplate}
              style={{ width: "4rem", textAlign: "center" }}
            />
          </TablaFiltrable>
        ) : (
          <p css={{ color: "var(--grisTextos)" }}>
            No hay reportes de ensayo registrados
          </p>
        )}
      </Card>

      {/* Sidebar Agregar */}
      <Sidebar
        visible={sidebarAgregar}
        onHide={() => setSidebarAgregar(false)}
        position="right"
        className="w-full lg:w-30rem md:w-30rem"
      >
        <Titulo>Nuevo reporte de ensayo</Titulo>
        <Formulario config={agregarFormConfig} loader={loaderAgregar} />
      </Sidebar>

      {/* Sidebar Detalles */}
      <Sidebar
        visible={sidebarDetalle}
        onHide={() => setSidebarDetalle(false)}
        position="right"
        className="w-full lg:w-30rem md:w-30rem"
      >
        {reporteSeleccionado && (
          <>
            <Titulo>Detalles del reporte</Titulo>
            <div className="grid">
              <CampoInformacion nombreCampo="Código">
                {reporteSeleccionado.codigo}
              </CampoInformacion>
              <CampoInformacion nombreCampo="Fecha de emisión">
                {formatFecha(reporteSeleccionado.fechaemision)}
              </CampoInformacion>
              <CampoInformacion nombreCampo="Laboratorio">
                {reporteSeleccionado.laboratorio?.nombrefantasia || "-"}
              </CampoInformacion>
              <CampoInformacion nombreCampo="Actividad">
                {reporteSeleccionado.actividad?.actividadtipo?.nombre || "-"}
              </CampoInformacion>
              <CampoInformacion nombreCampo="Fecha de actividad">
                {formatFecha(reporteSeleccionado.actividad?.fechacreacion)}
              </CampoInformacion>
            </div>
          </>
        )}
      </Sidebar>

      {/* Sidebar Editar */}
      <Sidebar
        visible={sidebarEditar}
        onHide={() => setSidebarEditar(false)}
        position="right"
        className="w-full lg:w-30rem md:w-30rem"
      >
        {editarFormConfig && (
          <>
            <Titulo>Editar reporte de ensayo</Titulo>
            <Formulario
              key={reporteSeleccionado?.id}
              config={editarFormConfig}
              loader={loaderEditar}
            />
          </>
        )}
      </Sidebar>
    </>
  );
}
